/**
 * @typedef {{ id: number; email: string; }} User
 * @typedef {import('amazon-cognito-identity-js').CognitoUser} CognitoUser
 * @typedef {import('amazon-cognito-identity-js').CognitoUserSession} CognitoUserSession
 */

import { Amplify, Auth } from 'aws-amplify';
import Cookies from 'js-cookie';

let configured = false;

function configureAmplify() {
    if (!configured && window.AMPLIFY_CONFIG) {
        Amplify.configure(
            Object.assign({}, window.AMPLIFY_CONFIG, {
                authenticationFlowType: 'CUSTOM_AUTH',
            })
        );

        configured = true;
    }

    return configured;
}

/**
 * @returns {Promise<CognitoUserSession>}
 */
export function refreshToken() {
    if (!configureAmplify()) {
        return Promise.reject(new Error('amplify not configured'));
    }

    return Auth.currentAuthenticatedUser().then(
        /**
         * @param {CognitoUser} currentUser
         */
        currentUser => {
            /** @type {CognitoUserSession | undefined} */
            const currentSession = currentUser.getSignInUserSession();

            if (!currentSession || !currentSession.isValid()) {
                return Promise.reject(new Error('invalid cognito session'));
            }

            return new Promise((resolve, reject) => {
                currentUser.refreshSession(currentSession.getRefreshToken(), (err, session) => (err ? reject(err) : resolve(session)));
            });
        }
    );
}

/**
 * @param {User} userToImpersonate
 * @returns {Promise<CognitoUser | undefined>} impersonated CognitoUser or `undefined` if impersonation failed
 */
export function impersonate(userToImpersonate) {
    if (!configureAmplify()) {
        return Promise.reject(new Error('amplify not configured'));
    }

    return Auth.currentAuthenticatedUser().then(
        /**
         * @param {CognitoUser} currentCognitoUser
         */
        currentCognitoUser =>
            Auth.signIn(userToImpersonate.email, undefined, { isImpersonating: 'true'})
                .then(
                    /**
                     * @param {CognitoUser} impersonatedCognitoUser
                     */
                    impersonatedCognitoUser => {
                        if (currentCognitoUser && impersonatedCognitoUser && impersonatedCognitoUser.challengeName === 'CUSTOM_CHALLENGE') {
                            const challengeResponse = currentCognitoUser.getSignInUserSession().getAccessToken().getJwtToken();

                            return Auth.sendCustomChallengeAnswer(impersonatedCognitoUser, challengeResponse, { isImpersonating: 'true'});
                        }
                    }
                )
                .then(
                    /**
                     * @param {CognitoUser | undefined} user
                     */
                    user => {
                        const session = user && user.getSignInUserSession();

                        if (session) {
                            Cookies.set('refreshToken', session.getRefreshToken().getToken());

                            const imp = localStorage.getItem('impersonators');

                            /** @type {string[]} */
                            const impersonators = imp ? JSON.parse(imp) : [];

                            impersonators.unshift(currentCognitoUser.getUsername());

                            localStorage.setItem('impersonators', JSON.stringify(impersonators));
                        }

                        return user;
                    }
                )
    );
}
